import { useEffect, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';

// MUI Components
import Drawer from '@mui/material/Drawer';
import Box from '@mui/material/Box';

// Layout Components
import Header from '../../components/layout/Header';

// Utils
import LeftNavigation, { TabsData } from './LeftNavigation';

import { Divider, IconButton, Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Stack from '@mui/material/Stack';
import { useSelector } from 'react-redux';
import { canAccessRoute } from '../../utils/permissions';
import { useGetTypeInProductsListQuery } from '../../store/apiSlices/typeInProducts/typeInProductsApis';
import { useUnsavedChanges } from '../../context/UnsavedChangesContext';
import UnsavedChangesModal from '../UnsavedChangesModal';

// Responsive drawer width with sensible min/max bounds
const drawerWidth = 'clamp(230px, 17vw, 280px)';
const stackWidth = '50px';

// Pages not listed in the navigation that should highlight their parent nav item
const NAV_PARENT_ROUTES: [string, string][] = [
  ['/deliveries/edit/', '/deliveries'],
  ['/rob/edit/', '/rob'],
  ['/lubrication-view', '/lubrication-charts'],
  ['/lubrication-edit', '/lubrication-charts'],
  ['/price-list-details', '/price-list'],
];

const resolveNavPath = (pathname: string) =>
  NAV_PARENT_ROUTES.find(([prefix]) => pathname.startsWith(prefix))?.[1] ?? pathname;

const MainLayout = () => {
  const { t } = useTranslation();
  const userInfo = useSelector((state: any) => state.user.userInfo);
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(true);
  const [mainTab, setMainTab] = useState<[number, number]>([0, 0]);
  const [subTab, setSubTab] = useState(0);
  const [activeMainTab, setActiveMainTab] = useState(1);
  const [navTabsData, setNavTabsData] = useState<TabsData[]>([]);

  // Unsaved changes context
  const {
    hasUnsavedChanges,
    showNavigationWarning,
    setShowNavigationWarning,
    pendingNavigation,
    setPendingNavigation,

    onLeaveWithoutSaving,
    clearUnsavedChanges,
  } = useUnsavedChanges();

  // Fetch type-in products count
  const { data: typeInProducts = [] } = useGetTypeInProductsListQuery(undefined, {
    refetchOnMountOrArgChange: true,
    refetchOnFocus: true,
    refetchOnReconnect: true,
  });
  const typeInProductsCount = typeInProducts.length;

  // Toggle drawer
  const handleDrawerToggle = () => setOpen((prev) => !prev);

  // Perform actual navigation
  const performNavigation = useCallback(
    (mainIdx: number, subIdx: number | null, route: string) => {
      setMainTab([mainIdx, typeof subIdx === 'number' ? subIdx : 0]);
      setSubTab(typeof subIdx === 'number' ? subIdx : 0);

      // Clear location state when navigating to ensure clean state
      // This is important for routes like /deliveries/add or /rob/add
      // to reset form data when coming from edit mode
      navigate(route, { state: null });
    },
    [navigate],
  );

  // Handle left navigation click with unsaved changes check
  const handleSidebarClick = useCallback(
    (mainIdx: number, subIdx: number | null, route: string) => {
      // Clear saved grid state for other modules, preserve state for the target module
      if (!route.startsWith('/deliveries')) {
        sessionStorage.removeItem('delivery_grid_state');
      }
      if (!route.startsWith('/rob')) {
        sessionStorage.removeItem('robs_grid_state');
      }

      // Check if there are unsaved changes
      if (hasUnsavedChanges) {
        // Store pending navigation and show warning modal
        setPendingNavigation({ mainIdx, subIdx, route });
        setShowNavigationWarning(true);
      } else {
        // No unsaved changes, proceed with navigation
        performNavigation(mainIdx, subIdx, route);
      }
    },
    [hasUnsavedChanges, setPendingNavigation, setShowNavigationWarning, performNavigation],
  );

  // Handle closing the warning modal
  const handleCloseWarningModal = useCallback(() => {
    setShowNavigationWarning(false);
    setPendingNavigation(null);
  }, [setShowNavigationWarning, setPendingNavigation]);

  // Handle leaving without saving
  const handleLeaveWithoutSaving = useCallback(() => {
    // Call the discard callback if provided
    if (onLeaveWithoutSaving) {
      onLeaveWithoutSaving();
    }

    // Clear unsaved changes state
    clearUnsavedChanges();

    // Proceed with pending navigation
    if (pendingNavigation) {
      performNavigation(
        pendingNavigation.mainIdx,
        pendingNavigation.subIdx,
        pendingNavigation.route,
      );
    }
  }, [onLeaveWithoutSaving, clearUnsavedChanges, pendingNavigation, performNavigation]);

  // --- Types for tabsData ---
  const tabsData: TabsData[] = useMemo(
    () => [
      {
        section: t('navigation.sections.quickAccess'),
        items: [
          {
            label: t('deliveries.addDelivery'),
            icon: (
              <img
                src={'/assets/icons/add_delivery_rob.svg'}
                alt="logo"
                style={{ width: '15px' }}
              />
            ),
            route: '/deliveries/add',
          },
          {
            label: t('robs.addRob'),
            icon: (
              <img
                src={'/assets/icons/add_delivery_rob.svg'}
                alt="logo"
                style={{ width: '15px' }}
              />
            ),
            route: '/rob/add',
          },
          {
            label: t('navigation.reports'),
            icon: <img src={'/assets/icons/reports.svg'} alt="logo" style={{ width: '15px' }} />,
            route: '/reports',
          },
        ],
      },
      {
        section: t('navigation.sections.viewRecords'),
        items: [
          {
            label: t('robs.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/rob',
          },
          {
            label: t('deliveries.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/deliveries',
          },
        ],
      },
      {
        section: t('navigation.sections.administration'),
        items: [
          {
            label: t('lubricationChart.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/lubrication-charts',
          },
          {
            label: t('lubricants.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            subNav: [
              { label: t('lubricants.products'), route: '/lubricants/products' },
              { label: t('lubricants.categories'), route: '/lubricants/categories' },
              { label: t('lubricants.types'), route: '/lubricants/types' },
              { label: t('lubricants.groups'), route: '/lubricants/groups' },
            ],
          },
          {
            label: t('companies.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            subNav: [
              { label: t('companies.makers'), route: '/companies/makers' },
              { label: t('companies.suppliers'), route: '/companies/suppliers' },
              { label: t('companies.customers'), route: '/companies/customer' },
            ],
          },
          {
            label: t('priceList.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/price-list',
          },
          {
            label: t('extraCosts.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/extra-costs',
          },
          {
            label: t('managementPeriods.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/management-periods',
          },
          {
            label: t('typeInProducts.title'),
            icon: (
              <img src={'/assets/icons/other_modules.svg'} alt="logo" style={{ width: '15px' }} />
            ),
            route: '/type-in-lubricants',
            count: typeInProductsCount, // Add count badge
          },
        ],
      },
    ],
    [typeInProductsCount, t],
  );

  // Filter nav items by excluded routes (item or its subNav routes)
  const filterTabsByRoutes = useCallback((tabs: TabsData[], routesToExclude: string[]) => {
    const shouldExcludeItem = (item: TabsData['items'][number]) => {
      if (item.route && routesToExclude.includes(item.route)) return true;
      if (item.subNav?.length) {
        const allSubRoutesExcluded = item.subNav.every((sub) =>
          routesToExclude.includes(sub.route),
        );
        if (allSubRoutesExcluded) return true;
        // Filter out excluded subNav entries but keep the parent if some remain
        return false;
      }
      return false;
    };
    return tabs
      .map((section) => ({
        ...section,
        items: section.items
          .filter((item) => !shouldExcludeItem(item))
          .map((item) => {
            if (!item.subNav?.length) return item;
            const filteredSubNav = item.subNav.filter(
              (sub) => !routesToExclude.includes(sub.route),
            );
            return filteredSubNav.length === 0 ? null : { ...item, subNav: filteredSubNav };
          })
          .filter((item): item is NonNullable<typeof item> => item !== null),
      }))
      .filter((section) => section.items.length > 0);
  }, []);

  useEffect(() => {
    // Don't set empty array if userInfo hasn't loaded yet
    if (!userInfo || !userInfo.authorityName) {
      return;
    }

    // Hide every route the role is not permitted to open (same permissions as the route guards)
    const allRoutes = tabsData.flatMap((section) =>
      section.items.flatMap((item) => [
        ...(item.route ? [item.route] : []),
        ...(item.subNav?.map((sub) => sub.route) ?? []),
      ]),
    );
    const routesToExclude = allRoutes.filter(
      (route) => !canAccessRoute(route, userInfo.authorityName),
    );
    setNavTabsData(filterTabsByRoutes(tabsData, routesToExclude));
  }, [userInfo, tabsData, filterTabsByRoutes]); // Include full userInfo object in dependencies

  // Auto detect active tab from current route
  useEffect(() => {
    if (!navTabsData?.length) return;

    let matched = false;
    const navPath = resolveNavPath(location.pathname);

    navTabsData.some((main, mainIdx) =>
      main.items.some((tab, itemIdx) => {
        if (tab.route === navPath) {
          setMainTab([mainIdx, itemIdx]);
          setSubTab(0);
          matched = true;
          return true;
        }

        if (tab.subNav) {
          const subIdx = tab.subNav.findIndex((sub) => sub.route === navPath);
          if (subIdx !== -1) {
            setMainTab([mainIdx, itemIdx]);
            setSubTab(subIdx);
            matched = true;
            return true;
          }
        }

        return false;
      }),
    );

    // Page has no nav item (e.g. not found / unauthorized) - clear the highlight
    if (!matched) {
      setMainTab([-1, -1]);
      setSubTab(-1);
    }
  }, [location.pathname, navTabsData]);

  const mainNavigation = [
    {
      icon: <img src="/assets/logo/Desk_icon.svg" alt="lubricants" width={'17px'} />,
      onClick: () => {
        navigate('/desk');
        setActiveMainTab(0);
      },
      title: t('navigation.desk'),
    },
    {
      icon: <img src="/assets/logo/Lubricants_icon.svg" alt="lubricants" width={'17px'} />,
      onClick: () => {
        setOpen(true);
        setActiveMainTab(1);
      },
      title: t('lubricants.title'),
    },
    {
      icon: <img src="/assets/logo/Setting_icon.svg" alt="lubricants" width={'17px'} />,
      onClick: () => {
        navigate('/settings');
        setActiveMainTab(2);
      },
      title: t('navigation.settings'),
    },
  ];

  return (
    <Box sx={{ display: 'flex' }}>
      <Stack
        sx={{ width: stackWidth, p: '16px', position: 'relative', zIndex: 10 }}
        alignItems="center"
      >
        <img src={'/assets/logo/CMRE_Logo.svg'} alt="logo" style={{ width: '80%' }} />
        <Divider sx={{ my: 2, width: '100%', height: '1px', borderStyle: 'dashed' }} />
        <Stack sx={{ direction: 'column', gap: 1 }}>
          {mainNavigation?.map((item, index) => (
            <Tooltip title={item.title} placement="right">
              <IconButton
                onClick={() => {
                  setActiveMainTab(index);
                  item.onClick();
                }}
                key={index}
                sx={{
                  color: activeMainTab === index ? theme.palette.leftNavigation.activeText : '',
                  backgroundColor:
                    activeMainTab === index
                      ? `${theme.palette.leftNavigation.activeBg} !important`
                      : '',
                  padding: '12px',
                }}
              >
                {item.icon}
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      </Stack>

      {/* Left navigation */}
      <Drawer
        variant="persistent"
        anchor="left"
        open={open}
        sx={{
          width: open ? drawerWidth : 0,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: open ? drawerWidth : 0,
            boxSizing: 'border-box',
            scrollbarWidth: 'none',
            boxShadow: theme.customShadows.header,
            border: 'none',
            position: 'fixed',
            left: `calc(${stackWidth} + 30px)`,
            top: 0,
            height: '100vh',
            transform: open ? 'translateX(0)' : `translateX(calc(-1 * ${drawerWidth}))`,
            transition: `transform ${theme.transitions.custom.drawer}`,
            willChange: 'transform',
            zIndex: 1,
          },
        }}
      >
        <LeftNavigation
          handleParentClick={handleSidebarClick}
          activeTab={mainTab}
          activeSubTab={subTab}
          tabsData={navTabsData}
          currentPath={location.pathname}
        />
      </Drawer>

      {/* Right Panel */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          marginLeft: open ? `-calc(${stackWidth} + ${drawerWidth})` : 0,
          transition: `margin-left ${theme.transitions.custom.drawer}`,
          willChange: 'margin-left',
          width: open
            ? `calc(100% - (${stackWidth} + ${drawerWidth}))`
            : `calc(100% - ${stackWidth})`,
          height: '100vh',
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Header toggleSidebar={handleDrawerToggle} />

        <Box
          sx={{
            height: 'calc(100vh - 59px)',
            overflowY: 'auto',
            position: 'relative',
            p: 3,
            py: 1,
            backgroundColor: theme.palette.header.background,
          }}
        >
          {/* Outlet */}
          <Outlet />
        </Box>
      </Box>
      <UnsavedChangesModal
        open={showNavigationWarning}
        onClose={handleCloseWarningModal}
        onLeaveWithoutSaving={handleLeaveWithoutSaving}
      />
    </Box>
  );
};

export default MainLayout;
